import { PageTitle } from 'components/PageTitle'
import { httpsCallable } from 'firebase/functions'
import { useFunctions } from 'hooks/auth'
import { useRootStore } from 'hooks/rootStore'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import { toast } from 'react-hot-toast'
import { FullPageSpinner } from 'components/Spinner'
import { useTranslationTyped } from 'i18n/i18n'

function getApplePodcastsUrl(rssUrl: string) {
  if (rssUrl === '') return rssUrl
  return rssUrl?.replace(/https?/, 'podcast')
}

function getDowncastPodcastsUrl(rssUrl: string) {
  if (rssUrl === '') return rssUrl
  return rssUrl?.replace(/https?/, 'downcast')
}

function PodcastButton({
  rssURL,
  image,
  label,
  onClick,
}: {
  rssURL: string
  image: string
  label: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
}) {
  const disabled = rssURL === ''
  return (
    <div
      className={classNames(
        'flex h-[100px] w-full flex-col items-center justify-center rounded-lg bg-surface p-5 text-center',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        disabled ? 'opacity-25' : ''
      )}
    >
      <a
        onClick={disabled ? undefined : onClick}
        href={rssURL}
        rel="noreferrer"
      >
        <img
          src={`assets/images/podcast/${image}.png`}
          className="inline"
          alt=""
        />
        <span className="mt-2 block text-xs font-medium">{label}</span>
      </a>
    </div>
  )
}

export const PodcastsComponent = ({
  titleOverride,
  contentClassName,
  titleClassName,
  subTextClassName,
}: {
  titleOverride?: string
  contentClassName?: string
  titleClassName?: string
  subTextClassName?: string
}) => {
  const { tt } = useTranslationTyped()
  const [rssURL, setRssURL] = useState('')

  const functions = useFunctions()

  useEffect(() => {
    httpsCallable<{ data: null }, string>(
      functions,
      'getRssURL'
    )({ data: null }).then((result) => {
      setRssURL(result.data)
    })
  }, [functions])

  return (
    <main
      id="main"
      className={classNames(
        'flex h-full flex-col items-stretch justify-stretch overflow-auto md:flex-row'
      )}
    >
      <div className="flex h-full flex-col justify-between align-middle">
        <div className={classNames('md:ml-10', 'md:mt-20', contentClassName)}>
          <h1
            className={classNames(
              'mb-5 mt-5 text-center md:mt-0 md:text-left',
              titleClassName,
              { 'text-4xl font-extrabold': !titleClassName }
            )}
          >
            {titleOverride ? titleOverride : tt.onboarding.podcast()}
          </h1>
          <p
            className={classNames(subTextClassName, {
              'mb-5 text-label-large text-sm text-core-on-tertiary':
                !subTextClassName,
            })}
          >
            {tt.onboarding.podcast_sub()}
          </p>
          <ul className="grid grid-flow-row grid-cols-4 gap-4">
            <li>
              <PodcastButton
                rssURL={rssURL}
                onClick={(e) => {
                  e.preventDefault()
                  navigator.clipboard.writeText(rssURL)
                  toast.success(tt.components.link_copied_to_clipboard(), {
                    id: 'link-copied',
                  })
                }}
                image="podcast_rss"
                label={tt.pages.student.podcasts.copy_rss_feed()}
              />
            </li>
            <li>
              <PodcastButton
                rssURL={getApplePodcastsUrl(rssURL)}
                onClick={(e) => {
                  e.preventDefault()
                  window.open(getApplePodcastsUrl(rssURL), '_blank')
                }}
                image="podcast_apple"
                label={tt.pages.student.podcasts.apple_podcasts()}
              />
            </li>
            <li>
              <PodcastButton
                rssURL={getDowncastPodcastsUrl(rssURL)}
                onClick={(e) => {
                  e.preventDefault()
                  window.open(getDowncastPodcastsUrl(rssURL), '_blank')
                }}
                image="podcast_downcast"
                label={tt.pages.student.podcasts.downcast()}
              />
            </li>
          </ul>
        </div>
        <div></div>
      </div>
    </main>
  )
}

export const PodcastsPage = () => {
  const { tt } = useTranslationTyped()
  const rootStore = useRootStore()
  useEffect(() => {
    const returnTo = rootStore.router.queryParams?.returnTo
    setTimeout(() => {
      rootStore.navigateToPodcastsPage({
        returnTo: returnTo,
      })
    }, 1000)
  }, [rootStore])
  return (
    <>
      <PageTitle title={tt.pages.student.podcasts.podcast()} />
      <FullPageSpinner />
    </>
  )
}
